import { ImageDrawer, Vector2, Size2, Component, UpdateContext, InputManager, Line, Circle } from "../Engine";
import { Line2 } from "../Engine/Math/Line2";
import { SoccerPlaygroundConfig, Sizes, HalfArea } from "./";

export class Football extends Component {

    private football: ImageDrawer;

    leftPost: Vector2 = Vector2.zero;
    rightPost: Vector2 = Vector2.zero;
    goalLine: Line2 | undefined;
    intersectionPoint: Circle | undefined;
    width: number = 0;
    height: number = 0;

    lineToGoalPostLeft: Line | undefined;
    lineToGoalPostRight: Line | undefined;
    lineOfBisectorAngle: Line | undefined;

    /** Pure-math lines used for geometry calculations (angle, intersection). */
    private mathLineLeft: Line2 | undefined;
    private mathLineRight: Line2 | undefined;
    private mathLineBisector: Line2 | undefined;

    angleOfBisectorRadians: number = 0;
    angleOfBisectorDegree: number = 0;

    velocity: Vector2 = Vector2.zero;
    speed = 100;
    rotation = 0;

    
    constructor(public config: SoccerPlaygroundConfig, public followMouse: boolean = false, public drawLinesToGoal = true, location: Vector2 = Vector2.zero) {
        super();

        const footballSize = 0.75 * this.config.conversionFactor;
        this.football = new ImageDrawer('Football.svg', Vector2.zero, new Size2(footballSize,footballSize));

        this.location = location;

        const realWidth = Sizes.GoalWidth;
        const realHeight = Sizes.GoalWidth / 6;

        this.width = realWidth * this.config.conversionFactor;
        this.height = realHeight * this.config.conversionFactor;

        const halfArea = this.halfArea;
        const yCoordinate = halfArea === HalfArea.UpperArea ? this.config.topLeftCorner.y : this.config.bottomLeftCorner.y;

        this.leftPost = new Vector2(config.centerPoint.x - (this.width / 2), yCoordinate);
        this.rightPost = new Vector2(config.centerPoint.x + (this.width / 2), yCoordinate);
        this.goalLine = new Line2(this.leftPost, this.rightPost);
    }

    get location() {
        return this.football.location;
    }
    set location(value: Vector2) {
        this.football.location = value;
    }

    get halfArea() : HalfArea {
        return this.location.y <= this.config.centerPoint.y ? HalfArea.UpperArea : HalfArea.LowerArea;
    }

    update(updateContext: UpdateContext): void {
        super.update(updateContext);
        
        this.football.update(updateContext);

        if (this.followMouse) {
            this.location = InputManager.getMousePosition();
        }

        const yCoordinate = this.halfArea === HalfArea.UpperArea ? this.config.topLeftCorner.y : this.config.bottomLeftCorner.y;

        this.leftPost = new Vector2(this.config.centerPoint.x - (this.width / 2), yCoordinate);
        this.rightPost = new Vector2(this.config.centerPoint.x + (this.width / 2), yCoordinate);
        this.goalLine = new Line2(this.leftPost, this.rightPost);

        // --- Math lines (Line2) for geometry calculations ---
        this.mathLineLeft ??= new Line2(this.leftPost, this.location);
        this.mathLineLeft.start = this.leftPost;
        this.mathLineLeft.end = this.location;

        this.mathLineRight ??= new Line2(this.rightPost, this.location);
        this.mathLineRight.start = this.rightPost;
        this.mathLineRight.end = this.location;

        const angleA = Math.atan2(this.leftPost.y - this.location.y, this.leftPost.x - this.location.x);
        const angleB = Math.atan2(this.rightPost.y - this.location.y, this.rightPost.x - this.location.x);
        const bisectorAngle = (angleA + angleB) / 2;

        const bisectorLength = 9999;
        const bisectorXEnd = this.location.x + bisectorLength * Math.cos(bisectorAngle);
        const bisectorYEnd = this.location.y + bisectorLength * Math.sin(bisectorAngle);

        this.mathLineBisector ??= new Line2(this.location, new Vector2(bisectorXEnd, bisectorYEnd));
        this.mathLineBisector.start = this.location;
        this.mathLineBisector.end = new Vector2(bisectorXEnd, bisectorYEnd);

        const newEndpoint = this.mathLineBisector.intersection(this.goalLine);

        if (newEndpoint) {
            this.mathLineBisector.end = newEndpoint;
        }

        // --- Drawable lines (Line) kept in sync for rendering ---
        this.lineToGoalPostLeft ??= new Line(this.leftPost, this.location);
        this.lineToGoalPostLeft.start = this.mathLineLeft.start;
        this.lineToGoalPostLeft.end = this.mathLineLeft.end;

        this.lineToGoalPostRight ??= new Line(this.rightPost, this.location);
        this.lineToGoalPostRight.start = this.mathLineRight.start;
        this.lineToGoalPostRight.end = this.mathLineRight.end;

        this.lineOfBisectorAngle ??= new Line(this.location, this.mathLineBisector.end);
        this.lineOfBisectorAngle.start = this.mathLineBisector.start;
        this.lineOfBisectorAngle.end = this.mathLineBisector.end;

        if (newEndpoint) {
            this.intersectionPoint ??= new Circle(newEndpoint, 2);
            this.intersectionPoint.center = newEndpoint;
        }

        this.angleOfBisectorRadians = this.mathLineLeft.angle(this.mathLineRight);
        this.angleOfBisectorDegree = Math.abs(this.angleOfBisectorRadians * 180 / Math.PI);

        if (this.velocity.isZero() === false) {
            this.location.x += this.velocity.x * updateContext.secondsPassed * this.speed;
            this.location.y += this.velocity.y * updateContext.secondsPassed * this.speed;
            this.rotation += 0.075;
            this.football.rotation = -this.rotation;
            this.football.update(updateContext);
        }
        else
        {
            this.rotation = 0;
            this.football.rotation = -this.rotation;
            this.football.update(updateContext);
        }
    }

    draw(ctx: CanvasRenderingContext2D): void {
        super.draw(ctx);
        
        this.football.draw(ctx);

        if (this.ShouldDrawAngleBisector()) {
            
            this.drawLines(ctx);
        }

    }

    public shootOnGoal() {
        if (this.mathLineLeft === undefined) return;
        if (this.mathLineRight === undefined) return;
        if (this.intersectionPoint === undefined) return;

        const angleToIntersectionPoint = Math.atan2(
            this.intersectionPoint.center.y - this.location.y,
            this.intersectionPoint.center.x - this.location.x
        );

        this.velocity = new Vector2(
            Math.cos(angleToIntersectionPoint),
            Math.sin(angleToIntersectionPoint)
        );
    }

    private drawLines(ctx: CanvasRenderingContext2D) {
        ctx.lineWidth = 0.5;

        if (this.config.showLineToPosts || this.followMouse == false) {
            this.lineToGoalPostLeft?.draw(ctx);
            this.lineToGoalPostRight?.draw(ctx);
        }

        if (this.config.showAngleOfBisectorLine || this.followMouse == false) {
            this.lineOfBisectorAngle?.draw(ctx);
        }


        this.intersectionPoint?.draw(ctx);

        const distance = this.mathLineBisector?.length() ?? 0;
        const realDistance = distance / this.config.conversionFactor;

        const offsetY = this.halfArea === HalfArea.UpperArea ? +10 : -5;

        const textLines = [
            `Distanz : ${realDistance.toFixed(1)}m`,
            `Winkel  : ${this.angleOfBisectorDegree.toFixed(1)}°`,
        ];

        textLines.forEach((text, i) => {
            ctx.fillStyle = 'white';
            ctx.font = '14px Consolas';
            ctx.fillText(text,
                (this.location.x ?? 0) + 10,
                (this.location.y ?? 0) + offsetY + (i + 1) * 14);
        });
    }

    private ShouldDrawAngleBisector(): boolean {

        if (this.lineToGoalPostLeft === undefined) return false;

        if(this.lineToGoalPostLeft.end.x < this.config.topLeftCorner.x ||
            this.lineToGoalPostLeft.end.x > this.config.topRightCorner.x
        ) {
            return false;
        }

        if(this.halfArea === HalfArea.UpperArea) {

            return this.lineToGoalPostLeft.end.y >= this.config.topLeftCorner.y 
            && this.lineToGoalPostLeft.end.y <= this.config.centerPoint.y;

        } else if (this.halfArea === HalfArea.LowerArea) {

            return this.lineToGoalPostLeft.end.y >= this.config.centerPoint.y 
            && this.lineToGoalPostLeft.end.y <= this.config.bottomLeftCorner.y;
        }

        return false;

    }

}